<script>
import {computed, onMounted, ref, watch} from "vue";
import axios from "axios";
import {useSecurityStore} from "@/stores/Security";
import useTools from "@/use/Tools";
import {useDataSelectStore} from "@/stores/DataSelect";
import {useEvaluationMeasureStore} from "@/stores/Evaluation/EvaluationMeasure";

export default {
    props: {
        value: Array,
        readonly: Boolean,
        initCategories: Array,
        type: String,
        activateCategoryGravity: Boolean,
        activateCriterionGravity: Boolean,
        activateTarget: Boolean,
        preSelectedNote: Boolean,
    },
    setup(props, {emit}) {
        
        const {$i18n, $router, $route} = useTools();
        const securityStore = useSecurityStore();
        const dataSelectStore = useDataSelectStore();
        const evaluationMeasureStore = useEvaluationMeasureStore();
        
        const categories = ref([]);
        
        const criterionPath = computed(() => `${$route.name}.criterion`);
        const criterionCategoryPath = computed(() => `${$route.name}.criterion_category`);
        
        const criterionActions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${criterionPath.value}.edit`, params: {id: item.id}})
                },
            }
        ];
        const criterionCategoryActions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${criterionCategoryPath.value}.edit`, params: {id: item.id}})
                },
            }
        ];
        
        const headers = [
            {
                text: "...",
                align: "left",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.name"),
                align: "left",
                sortable: true,
                value: "name",
            },
            {
                text: "",
                align: "left",
                sortable: true,
                value: "state",
            },
        ];
        const categorySearch = ref(null);
        const measureSearch = ref(null);
        
        const gravities = ref([]);
        const gravityDialog = ref(false);
        
        function findAllGravities() {
            axios.get(`/evaluationGravities`, {params: {type: props.type}}).then(response => {
                gravities.value = response.data;
            });
        }
        
        
        const selectedCriterion = ref({});
        
        function selectCriterion(criterion) {
            selectedCriterion.value = criterion;
            gravityDialog.value = true;
        }
        
        function setGravityTarget(gravity) {
            selectedCriterion.value.target = gravity.note;
            selectedCriterion.value.targetByGravityId = gravity.id;
            selectedCriterion.value.targetByGravityName = gravity.name;
            
            const category = categories.value.find(cat => cat.criteria.some(c => c === selectedCriterion.value));
            
            if (category) {
                // Find the criterion index in that category
                const index = category.criteria.findIndex(c => c === selectedCriterion.value);
                
                if (index !== -1)
                    category.criteria.splice(index, 1, {...selectedCriterion.value});
                
            }
            
            gravityDialog.value = false;
        }
        
        const categoryDialog = ref(false);
        const selectedCategory = ref({criteria: []});
        
        function findCategories() {
            axios.get(`/dataSelects`, {
                params: {type: `${props.type}_CRITERION_CATEGORY`,}
            }).then(response => {
                dataSelectStore.setDataSelects(response.data);
            });
        }
        
        function manageCategory(category) {
            if (isCategorySelected(category)) {
                removeCategory(category);
            } else {
                addCategory(category)
            }
        }
        
        function addCategory(category) {
            categories.value.push({
                categoryId: category.id,
                categoryName: category.name,
                gravity: 1.0,
                criteria: []
            });
        }
        
        function removeCategory(category) {
            let index = categories.value.findIndex(e => e.categoryId === category.id);
            categories.value.splice(index, 1);
        }
        
        function isCategorySelected(category) {
            return categories.value.findIndex(e => e.categoryId === category.id) !== -1;
        }
        
        function selectCategory(category) {
            selectedCategory.value = category;
            measureDialog.value = true;
        }
        
        function moveUpCategory(index) {
            if (index > 0) {
                const temp = categories.value[index];
                categories.value.splice(index, 1);
                categories.value.splice(index - 1, 0, temp);
            }
        }
        
        function moveDownCategory(index) {
            if (index < categories.value.length - 1) {
                const temp = categories.value[index];
                categories.value.splice(index, 1);
                categories.value.splice(index + 1, 0, temp);
            }
        }
        
        const measureDialog = ref(false);
        
        const measuresByCategory = computed(() => {
            const allMeasures = evaluationMeasureStore.evaluationMeasures;
            
            return allMeasures.filter(measure => {
                const isSelectedInCurrentCategory = selectedCategory.value.criteria.some(c => c.measureId === measure.id);
                
                const isSelectedAnywhere = isMeasureSelected(measure);
                
                // Return measure if:
                // 1. It's already selected in the current category, OR
                // 2. It's not selected anywhere in the evaluation file
                return isSelectedInCurrentCategory || !isSelectedAnywhere;
            });
        });
        
        function findMeasures() {
            axios.get(`/evaluationMeasures`, {
                params: {
                    type: `${props.type}`,
                    sortBy: 'id'
                }
            }).then(response => {
                evaluationMeasureStore.setEvaluationMeasures(response.data);
            });
        }
        
        function manageMeasure(measure) {
            if (isMeasureSelected(measure)) {
                removeMeasure(measure);
            } else {
                addMeasure(measure)
            }
        }
        
        function addMeasure(measure) {
            selectedCategory.value.criteria.push({
                measureId: measure.id,
                name: measure.name,
                notes: measure.notes,
                measureKeyPerformanceIndicator: measure.keyPerformanceIndicator,
                target: 0.0,
                gravity: 1.0,
                deadline: null,
            });
        }
        
        function removeMeasure(measure) {
            let index = selectedCategory.value.criteria.findIndex(e => e.measureId === measure.id);
            selectedCategory.value.criteria.splice(index, 1);
        }
        
        function isMeasureSelected(criterion) {
            return categories.value.some(category =>
                category.criteria.some(c => c.measureId === criterion.id)
            );
        }
        
        function moveUpMeasure(index, category) {
            if (index > 0) {
                const temp = category.criteria[index];
                category.criteria.splice(index, 1);
                category.criteria.splice(index - 1, 0, temp);
            }
        }
        
        function moveDownMeasure(index, category) {
            if (index < category.criteria.length - 1) {
                const temp = category.criteria[index];
                category.criteria.splice(index, 1);
                category.criteria.splice(index + 1, 0, temp);
            }
        }
        
        watch(() => props.initCategories, (newVal) => {
            if (newVal && newVal.length > 0) {
                categories.value = JSON.parse(JSON.stringify(newVal));
            }
        }, {deep: true, immediate: true});
        
        watch(() => categories.value, () => {
            emit('input', categories.value);
        }, {deep: true});
        
        onMounted(() => {
            findAllGravities();
            findCategories();
            findMeasures();
        });
        
        return {
            dataSelectStore,
            evaluationMeasureStore,
            categories,
            headers,
            criterionActions,
            criterionCategoryActions,
            categorySearch,
            measureSearch,
            gravities,
            gravityDialog,
            measuresByCategory,
            selectedCategory,
            categoryDialog,
            measureDialog,
            criterionPath,
            criterionCategoryPath,
            selectedCriterion,
            selectCriterion,
            setGravityTarget,
            manageCategory,
            isCategorySelected,
            selectCategory,
            moveUpCategory,
            moveDownCategory,
            manageMeasure,
            isMeasureSelected,
            moveUpMeasure,
            moveDownMeasure,
        }
    }
}
</script>

<template>
    <div>
        <w-fieldset
            title="Sections"
            :show-add-action="!readonly"
            icon="mdi-pencil-circle-outline"
            @add="categoryDialog = true"
        >
            <v-simple-table dense class="bordered-table">
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th class="text-center" v-if="!readonly && categories.length > 1"></th>
                        <th class="text-center" v-if="!readonly  && categories.length > 1"></th>
                        <th class="text-center">{{ $t('evaluation.section') }}</th>
                        <th class="text-center" v-if="activateCategoryGravity">{{ $t('common.gravity') }}</th>
                        <th class="text-center" v-if="!readonly"></th>
                        
                        <th class="text-center" v-if="!readonly"></th>
                        <th class="text-center" v-if="!readonly"></th>
                        <th class="text-center">{{ $t('evaluation.criterion') }}</th>
                        <th class="text-center" v-if="type === 'EVALUATION_PERFORMANCE'">{{ $t('common.kpi') }}</th>
                        <th class="text-center" v-if="type === 'EVALUATION_PERFORMANCE'">{{ $t('common.deadline') }}</th>

                        <th class="text-center" v-if="activateCriterionGravity">{{ $t('common.gravity') }}</th>
                        <th class="text-center" v-if="activateTarget">{{ $t('evaluation.target') }}</th>
                    
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="(item, catIndex) in categories">
                        <!-- Always render at least one row per category -->
                        <tr :key="`c-${catIndex}-0`">
                            <!-- Category Name - always visible with rowspan -->
                            <td
                                v-if="!readonly  && categories.length > 1"
                                class="text-center ma-0 pa-0"
                                :rowspan="Math.max(1, item.criteria.length)"
                            >
                                <w-btn-icon
                                    v-if="catIndex > 0"
                                    icon="mdi-arrow-up-circle-outline"
                                    color="blue"
                                    @press="moveUpCategory(catIndex)"
                                />
                            </td>
                            <td
                                v-if="!readonly && categories.length > 1"
                                class="text-center ma-0 pa-0"
                                :rowspan="Math.max(1, item.criteria.length)"
                            >
                                <w-btn-icon
                                    v-if="catIndex < categories.length - 1"
                                    icon="mdi-arrow-down-circle-outline"
                                    color="blue"
                                    @press="moveDownCategory(catIndex)"
                                />
                            </td>
                            <td
                                class="text-center ma-0 pa-0"
                                style="background-color: #D7CCC8"
                                :rowspan="Math.max(1, item.criteria.length)"
                            >
                                <strong>{{ item.categoryName }}</strong>
                            </td>
                            
                            <!-- Gravity - always visible with rowspan -->
                            <td
                                v-if="activateCategoryGravity"
                                style="background-color: #EF9A9A"
                                class="text-center  ma-0 pa-0"
                                :rowspan="Math.max(1, item.criteria.length)"
                            >
                                <w-edit-dialog
                                    v-model="item.gravity"
                                    type="number"
                                    :readonly="readonly"
                                    hide-details
                                />
                            </td>
                            
                            <!-- Add Button - always visible with rowspan -->
                            <td
                                v-if="!readonly"
                                class="text-center ma-0 pa-0"
                                style="background-color: #90CAF9"
                                :rowspan="Math.max(1, item.criteria.length)"
                            >
                                <w-btn-icon
                                    v-if="!readonly"
                                    color="teal"
                                    icon="mdi-pencil-circle-outline"
                                    @press="selectCategory(item)"
                                />
                            </td>
                            
                            <!-- First criterion or empty cells -->
                            <template v-if="item.criteria.length > 0">
                                <!-- Criterion Name -->
                                <td
                                    v-if="!readonly"
                                    class="text-center ma-0 pa-0"
                                >
                                </td>
                                <td
                                    v-if="!readonly"
                                    class="text-center ma-0 pa-0"
                                >
                                    <w-btn-icon
                                        color="blue"
                                        icon="mdi-arrow-down-circle-outline"
                                        @press="moveDownMeasure(0, item)"
                                    />
                                </td>
                                <td>
                                    {{ item.criteria[0].name }}
                                    <w-btn-icon-help :text="item.criteria[0].notes"/>
                                </td>
                                <td v-if="type === 'EVALUATION_PERFORMANCE'">
                                    {{item.criteria[0].measureKeyPerformanceIndicator}}
                                </td>
                                <td v-if="type === 'EVALUATION_PERFORMANCE'">
                                    <w-edit-dialog
                                        v-model="item.criteria[0].deadline"
                                        large
                                        date-format
                                        :readonly="readonly"
                                    />
                                </td>
                                <!-- Criterion Gravity -->
                                <td
                                    v-if="activateCriterionGravity"
                                    class="text-center"
                                >
                                    <w-edit-dialog
                                        v-model="item.criteria[0].gravity"
                                        :readonly="readonly"
                                        type="number"
                                        hide-details
                                    />
                                </td>
                                
                                <!-- Criterion Target -->
                                <td class="text-center"  v-if="activateTarget">
                                    
                                    <w-chip
                                        v-if="preSelectedNote"
                                        :text="item.criteria[0].targetByGravityName ? item.criteria[0].targetByGravityName : $t('evaluation.click_to_chose')"
                                        icon="mdi-pencil"
                                        icon-color="orange"
                                        outlined
                                        :readonly="readonly"
                                        @press="selectCriterion(item.criteria[0])"
                                    />
                                    <w-edit-dialog
                                        v-else
                                        v-model="item.criteria[0].target"
                                        type="number"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </td>
                            </template>
                            
                            <!-- Empty cells when no criteria -->
                            <template v-else>
                                <td
                                    v-if="!readonly"
                                    class="text-center"
                                >
                                    -
                                </td>
                                <td
                                    v-if="!readonly"
                                    class="text-center"
                                >
                                    -
                                </td>

                                <td>
                                    <em class="grey--text">{{ $t('evaluation.no_criteria_added') }}</em>
                                </td>
                                <td
                                    v-if="!readonly && type === 'EVALUATION_PERFORMANCE'"
                                    class="text-center"
                                >
                                    -
                                </td>
                                <td
                                    v-if="!readonly && type === 'EVALUATION_PERFORMANCE'"
                                    class="text-center"
                                >
                                    -
                                </td>
                                <td
                                    v-if="activateCriterionGravity"
                                    style="background-color: #FFCC80"
                                    class="text-center"
                                >
                                    -
                                </td>
                                <td
                                    v-if="activateTarget"
                                    style="background-color: #80CBC4"
                                    class="text-center"
                                >
                                    -
                                </td>
                            </template>
                        </tr>
                        
                        <!-- Additional rows for remaining criteria (skip first one) -->
                        <tr
                            v-for="(criterion, critIndex) in item.criteria.slice(1)"
                            :key="`c-${catIndex}-${critIndex + 1}`"
                        >
                            
                            <td
                                v-if="!readonly"
                                class="text-center ma-0 pa-0"
                            >
                                <w-btn-icon
                                    v-if="critIndex + 1 > 0"
                                    icon="mdi-arrow-up-circle-outline"
                                    color="blue"
                                    @press="moveUpMeasure(critIndex + 1, item)"
                                />
                            </td>
                            <td
                                v-if="!readonly"
                                class="text-center ma-0 pa-0"
                            >
                                <w-btn-icon
                                    v-if="critIndex + 1 < item.criteria.length - 1"
                                    icon="mdi-arrow-down-circle-outline"
                                    color="blue"
                                    @press="moveDownMeasure(critIndex + 1, item)"
                                />
                            </td>
                            
                            <!-- Criterion Name -->
                            <td>
                                {{ criterion.name }}
                                <w-btn-icon-help :text="criterion.notes"/>

                            </td>
                            <td v-if="type === 'EVALUATION_PERFORMANCE'">
                                {{criterion.measureKeyPerformanceIndicator}}
                            </td>
                            <td v-if="type === 'EVALUATION_PERFORMANCE'">
                                <w-edit-dialog
                                    v-model="criterion.deadline"
                                    large
                                    date-format
                                    :readonly="readonly"
                                />
                            </td>
                            <!-- Criterion Gravity -->
                            <td
                                v-if="activateCriterionGravity"
                                class="text-center"
                            >
                                <w-edit-dialog
                                    v-model="criterion.gravity"
                                    :readonly="readonly"
                                    type="number"
                                    hide-details
                                />
                            </td>
                            
                            <!-- Criterion Target -->
                            <td
                                v-if="activateTarget"
                                class="text-center"
                            >
                                <w-chip
                                    v-if="preSelectedNote"
                                    :text="criterion.targetByGravityName ? criterion.targetByGravityName : $t('evaluation.click_to_chose')"
                                    icon="mdi-pencil"
                                    icon-color="orange"
                                    outlined
                                    :readonly="readonly"
                                    @press="selectCriterion(criterion)"
                                />

                                <w-edit-dialog
                                    v-else
                                    v-model="criterion.target"
                                    type="number"
                                    :readonly="readonly"
                                    hide-details
                                />
                            </td>
                        </tr>
                        
                        <!-- Spacing row after each category -->
                        <tr v-if="catIndex < categories.length - 1" :key="`spacer-${catIndex}`">
                            <td colspan="100%" style="height: 10px; background-color: transparent;"></td>
                        </tr>
                    </template>
                    </tbody>
                </template>
            </v-simple-table>
        </w-fieldset>
        
        <v-dialog
            v-model="gravityDialog"
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="red" :title="$t('module.evaluation_gravities')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-list dense>
                            <template v-for="(gravity, index) in gravities">
                                <v-list-item
                                    :key="`gravity-${index}`"
                                    @click="setGravityTarget(gravity)"
                                    two-line
                                >
                                    <v-list-item-content>
                                        <v-list-item-title>{{ gravity.name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ gravity.description }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-icon
                                            v-if="gravity.id === selectedCriterion.targetByGravityId"
                                            color="teal"
                                        >
                                            mdi-check-circle
                                        </v-icon>
                                    </v-list-item-action>
                                </v-list-item>
                                <v-divider :key="`d-gravity-${index}`"/>
                            </template>
                        </v-list>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        
        <v-dialog
            v-model="categoryDialog"
            scrollable
            persistent
            max-width="800"
        >
            <v-card>
                <w-toolbar color="teal" title="Section">
                    <template #right>
                        <v-btn
                            icon
                            class="white--text"
                            @click="$router.push({name: `${criterionCategoryPath}.add`})"
                        >
                            <v-icon>mdi-plus-circle-outline</v-icon>
                        </v-btn>
                    </template>
                </w-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="categorySearch"
                                    :label="$t('common.search')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                    <w-data-table
                        :headers="headers"
                        :actions="criterionCategoryActions"
                        :items="dataSelectStore.dataSelects"
                        :search="categorySearch"
                        dense
                        disable-pagination
                        fixed-header
                        height="500"
                        @rowDbClick="manageCategory"
                    >
                        <template v-slot:[`item.state`]="{ item }">
                            <v-icon v-if="isCategorySelected(item)" color="teal">mdi-check-circle</v-icon>
                        </template>
                    </w-data-table>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="categoryDialog = false"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog
            v-model="measureDialog"
            scrollable
            persistent
            max-width="800"
        >
            <v-card>
                <w-toolbar color="teal" :title="selectedCategory.categoryName">
                    <template #right>
                        <v-btn
                            icon
                            class="white--text"
                            @click="$router.push({name: `${criterionPath}.add`})"
                        >
                            <v-icon>mdi-plus-circle-outline</v-icon>
                        </v-btn>
                    </template>
                </w-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="measureSearch"
                                    :label="$t('common.search')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                    <w-data-table
                        :headers="headers"
                        :actions="criterionActions"
                        :items="measuresByCategory"
                        :search="measureSearch"
                        dense
                        disable-pagination
                        fixed-header
                        height="500"
                        @rowDbClick="manageMeasure"
                    >
                        <template v-slot:[`item.state`]="{ item }">
                            <v-icon v-if="isMeasureSelected(item)" color="teal">mdi-check-circle</v-icon>
                        </template>
                    </w-data-table>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="measureDialog = false"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <input type="hidden">
    </div>
</template>

<style scoped>
.bordered-table table {
    border-collapse: collapse;
}


.bordered-table td {
    border: 1px solid #757575;
}
</style>